import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getDbHeader, getHeader } from "../../utils/util";
import { toast } from "react-toastify";
import BackBtn from "../../widgets/BackBtn";

function GtagConfigForm() {
  const [trackingId, setTrackingId] = useState("");
  const [gtmId, setGtmId] = useState("");
  const [googleSiteVerification, setGoogleSiteVerification] = useState("");
  const [anonymizeIp, setAnonymizeIp] = useState(true);
  const [sendPageView, setSendPageView] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_ENDPOINT}/event/get-gtag-config`, {
      ...getDbHeader(),
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.gtag) {
          setTrackingId(data.gtag.trackingId || "");
          setGtmId(data.gtag.gtmId || "");
          setGoogleSiteVerification(data.gtag.googleSiteVerification || "");
          setAnonymizeIp(data.gtag.additionalConfig?.anonymize_ip ?? true);
          setSendPageView(data.gtag.additionalConfig?.send_page_view ?? true);
        }
      })
      .catch(() => toast.error("Failed to load existing configuration."));
  }, []);

  const validate = () => {
    if (!trackingId && !gtmId) {
      toast.error(
        "Please provide either a Google Analytics Tracking ID or a GTM ID."
      );
      return false;
    }
    if (trackingId && !trackingId.startsWith("G-")) {
      toast.error(
        "Invalid Google Analytics Tracking ID. It must start with 'G-'."
      );
      return false;
    }
    if (gtmId && !gtmId.startsWith("GTM-")) {
      toast.error("Invalid Google Tag Manager ID. It must start with 'GTM-'.");
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    fetch(`${API_ENDPOINT}/event/save-gtag-config`, {
      method: "POST",
      ...getHeader(ADMIN),
      body: JSON.stringify({
        trackingId,
        gtmId,
        googleSiteVerification,
        additionalConfig: {
          anonymize_ip: anonymizeIp,
          send_page_view: sendPageView,
        },
      }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(d));
        return res.json();
      })
      .then((data) => toast.success(data.message))
      .catch((err) =>
        toast.error(err?.error || "Network error. Please try again.")
      )
      .finally(() => setLoading(false));
  };

  return (
    <Container sx={{ py: 4 }}>
      <BackBtn />
      <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: "auto" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Configure Tracking & SEO Settings
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Set up Google Analytics (<code>gtag.js</code>), Google Tag Manager,
          and Search Console verification. At least one tracking ID is required.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Google Analytics Tracking ID"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., G-XXXXXXXXXX"
            helperText="Required only if not using GTM. Must start with 'G-'."
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="The unique ID (e.g., G-XXXXXXXXXX) from your Google Analytics property."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
          />

          <TextField
            label="Google Tag Manager ID"
            value={gtmId}
            onChange={(e) => setGtmId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., GTM-XXXXXXX"
            helperText="Required only if not using a direct GA ID. Must start with 'GTM-'."
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="The unique ID (e.g., GTM-XXXXXXX) from your GTM container."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            Search Console Verification
          </Typography>
          <TextField
            label="Google Site Verification Content"
            value={googleSiteVerification}
            onChange={(e) => setGoogleSiteVerification(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., ghaAdeApmRzxUDMvKDIKsKOUZaQYX5He9lX1Xvi5DaE"
            helperText="Paste the exact string found inside the 'content' attribute of your meta tag."
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="This value will be injected as <meta name='google-site-verification' content='...' /> in the document head."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={anonymizeIp}
                  onChange={(e) => setAnonymizeIp(e.target.checked)}
                />
              }
              label="Anonymize IP Addresses"
            />
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ ml: 7, mb: 2 }}
          >
            {anonymizeIp
              ? "IP addresses will be anonymized for better privacy."
              : "Full IP addresses will be tracked for precise location data."}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={sendPageView}
                  onChange={(e) => setSendPageView(e.target.checked)}
                />
              }
              label="Send Page Views Automatically (gtag)"
            />
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ ml: 7, mb: 2 }}
          >
            {sendPageView
              ? "Initial page load is tracked automatically by gtag."
              : "Page views must be tracked manually (e.g., on route changes)."}
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ mt: 3 }}
          >
            {loading ? "Saving…" : "Save Configuration"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default GtagConfigForm;
